import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Avatar, Button, Grid, MenuItem, Stack, Tab, Tabs, TextField,
  Typography, FormControlLabel, Switch, Box,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneNumberField from '../ui/forms/PhoneNumberField';
import FormDrawer from '../ui/FormDrawer';
import ZoneMasterSelect from '../masters/ZoneMasterSelect';
import RestaurantLocationPicker from '../food/RestaurantLocationPicker';
import { getMasters } from '../../services/platform/masterService';
import { listPartners, getPartnerDocuments } from '../../services/partnerUserService';
import { listPartnerSubscriptionPlans } from '../../services/partnerSubscriptionPlanService';
import { resolveAssetUrl } from '../../utils/assetUrl';
import {
  storeHoursFromMasters, resolveStoreLogoFromMasters, type StoreHourRow, type GroceryMasterRow,
} from '../../utils/groceryMasterImages';
import { extractMerchantCompliance, hasMerchantCompliance } from '../../utils/merchantComplianceExtract';
import { formatMoney } from '../../utils/currency';
import {
  emptyGroceryStoreForm, storeRowToForm, storeFormToPayload,
  GROCERY_STORE_TABS, VERIFICATION_STATUS_OPTIONS, GROCERY_WEEK_DAYS,
  type GroceryStoreFormState,
} from './groceryStoreMasterTypes';

export type { GroceryStoreFormState };
export { emptyGroceryStoreForm, storeRowToForm, storeFormToPayload };

async function loadMasters(type: string): Promise<GroceryMasterRow[]> {
  const res = await getMasters(type, { limit: 300 });
  return (res.data || []) as GroceryMasterRow[];
}

interface GroceryStoreAddDrawerProps {
  open: boolean;
  editing: Record<string, unknown> | null;
  form: GroceryStoreFormState;
  onFormChange: (next: GroceryStoreFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  errorMessage?: string | null;
}

export default function GroceryStoreAddDrawer({
  open, editing, form, onFormChange, onClose, onSubmit, loading, errorMessage,
}: GroceryStoreAddDrawerProps) {
  const [tab, setTab] = useState(0);
  const patch = (partial: Partial<GroceryStoreFormState>) => onFormChange({ ...form, ...partial });

  const { data: masters, isLoading: mastersLoading } = useQuery({
    queryKey: ['grocery-store-add-masters'],
    queryFn: async () => ({
      grocery_store_types: await loadMasters('grocery_store_types'),
      warehouses: await loadMasters('warehouses'),
      grocery_availability_schedules: await loadMasters('grocery_availability_schedules'),
    }),
    enabled: open,
  });

  const { data: merchants = [], isFetched: merchantsFetched, isFetching: merchantsFetching } = useQuery({
    queryKey: ['merchant-partners-select'],
    queryFn: async () => {
      const res = await listPartners({ entityType: 'merchant', limit: 200 });
      return res.data;
    },
    enabled: open,
    staleTime: 60_000,
  });

  const merchantsReady = !open || (merchantsFetched && !merchantsFetching);

  const merchantOptions = useMemo(() => {
    const byId = new Map(merchants.map((m) => [m._id, m]));
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];
    const push = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label });
    };
    if (form.merchantId) {
      const linked = byId.get(form.merchantId);
      push(
        form.merchantId,
        linked
          ? `${linked.firstName} ${linked.lastName || ''} · ${linked.phone || linked.email}`
          : `Linked merchant (${form.merchantId.slice(-6)})`,
      );
    }
    for (const m of merchants) {
      push(m._id, `${m.firstName} ${m.lastName || ''} · ${m.phone || m.email}`);
    }
    return out;
  }, [form.merchantId, merchants]);

  const merchantSelectValue = form.merchantId && merchantOptions.some((o) => o.id === form.merchantId)
    ? form.merchantId
    : '';

  const { data: merchantPlans = [], isFetched: merchantPlansFetched } = useQuery({
    queryKey: ['merchant-subscription-plans-grocery', form.zoneCode],
    queryFn: () => listPartnerSubscriptionPlans({ partnerType: 'merchant', active: true }),
    enabled: open && form.revenueModel === 'subscription',
    staleTime: 60_000,
  });

  const merchantPlansReady = form.revenueModel !== 'subscription' || !open || merchantPlansFetched;

  const subscriptionPlanOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];
    const push = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label });
    };
    if (form.partnerSubscriptionPlanId) {
      const linked = merchantPlans.find((p) => p._id === form.partnerSubscriptionPlanId);
      push(
        form.partnerSubscriptionPlanId,
        linked
          ? `${linked.name} · ${formatMoney(linked.price)}/${linked.billingInterval} · ${linked.zoneCode}`
          : `Current plan (${form.partnerSubscriptionPlanId.slice(-6)})`,
      );
    }
    for (const p of merchantPlans) {
      push(p._id, `${p.name} · ${formatMoney(p.price)}/${p.billingInterval} · ${p.zoneCode}`);
    }
    return out;
  }, [form.partnerSubscriptionPlanId, merchantPlans]);

  const subscriptionPlanValue = merchantPlansReady
    && subscriptionPlanOptions.some((o) => o.id === form.partnerSubscriptionPlanId)
    ? form.partnerSubscriptionPlanId
    : '';

  const { data: merchantComplianceData } = useQuery({
    queryKey: ['merchant-compliance', form.merchantId],
    queryFn: () => getPartnerDocuments(form.merchantId, 'merchant'),
    enabled: open && !!form.merchantId,
  });

  const merchantCompliance = useMemo(
    () => (merchantComplianceData ? extractMerchantCompliance(merchantComplianceData) : null),
    [merchantComplianceData],
  );

  const applyMerchantCompliance = (includeLocation = false) => {
    if (!merchantCompliance) return;
    patch({
      gstNumber: merchantCompliance.gstNumber || form.gstNumber,
      fssaiNumber: merchantCompliance.fssaiNumber || form.fssaiNumber,
      tradeLicenseNumber: merchantCompliance.tradeLicenseNumber || form.tradeLicenseNumber,
      ownerName: merchantCompliance.ownerName || form.ownerName,
      addressLine: merchantCompliance.address || form.addressLine,
      city: merchantCompliance.city || form.city,
      ...(includeLocation && merchantCompliance.latitude ? { latitude: merchantCompliance.latitude } : {}),
      ...(includeLocation && merchantCompliance.longitude ? { longitude: merchantCompliance.longitude } : {}),
    });
  };

  useEffect(() => {
    if (open) setTab(0);
  }, [open, editing?._id]);

  useEffect(() => {
    if (!open || editing) return;
    const templates = masters?.grocery_availability_schedules || [];
    if (templates.length && form.openingHours.every((h) => h.openTime === '07:00' && h.closeTime === '22:00')) {
      patch({ openingHours: storeHoursFromMasters(templates) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, masters?.grocery_availability_schedules]);

  useEffect(() => {
    if (!open || mastersLoading) return;
    const types = masters?.grocery_store_types || [];
    if (!types.length) return;
    if (!form.storeTypeCode || !types.some((t) => t.code === form.storeTypeCode)) {
      const fromExisting = types.find(
        (t) => t.code === form.storeTypeCode || t.name.toLowerCase() === String(form.storeTypeCode || '').toLowerCase(),
      );
      patch({ storeTypeCode: fromExisting?.code || types[0].code });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?._id, mastersLoading, masters?.grocery_store_types]);

  useEffect(() => {
    if (!open || editing || form.logo) return;
    const url = resolveStoreLogoFromMasters(form.storeTypeCode, masters?.grocery_store_types || []);
    if (url) patch({ logo: url });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.storeTypeCode, open, editing, masters?.grocery_store_types]);

  const isDarkType = form.storeTypeCode === 'dark_store' || form.storeTypeCode.includes('dark');

  const updateHour = (index: number, partial: Partial<StoreHourRow>) => {
    const openingHours = [...form.openingHours];
    openingHours[index] = { ...openingHours[index], ...partial };
    patch({ openingHours });
  };

  const applyMasterHours = () => {
    const templates = masters?.grocery_availability_schedules || [];
    if (templates.length) patch({ openingHours: storeHoursFromMasters(templates) });
  };

  const documentsUrl = form.merchantId
    ? `/config/documents?entityType=merchant&partnerId=${form.merchantId}`
    : '/config/documents?entityType=merchant';

  const missingType = !form.storeTypeCode;
  const missingSubPlan = form.revenueModel === 'subscription' && !form.partnerSubscriptionPlanId;
  const canSubmit = !!form.name && !missingType && !missingSubPlan;

  return (
    <FormDrawer
      open={open}
      title={editing ? 'Edit Grocery Store' : 'Add Grocery Store'}
      subtitle="Professional store profile — identity, location, compliance, timing, and revenue."
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={editing ? 'Save' : 'Create'}
      loading={loading}
      disableSubmit={!canSubmit}
      width={720}
    >
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
      {!canSubmit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {!form.name && 'Store name is required. '}
          {missingType && 'Select a Store Type on the Business tab. '}
          {missingSubPlan && 'Select a subscription plan on the Approval tab. '}
        </Alert>
      )}
      {!mastersLoading && !(masters?.grocery_store_types?.length) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Seed grocery masters first: Delivery → Grocery Delivery → Masters.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {GROCERY_STORE_TABS.map((t) => (
          <Tab key={t.id} label={t.label} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
        ))}
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700}>Basic Details</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Identity, contact, location, and branding.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Store Name" value={form.name}
              onChange={(e) => patch({ name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/\s+/g, '-') })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PersonNameField fullWidth label="Owner Name" value={form.ownerName}
              onChange={(e) => patch({ ownerName: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Slug" value={form.slug}
              onChange={(e) => patch({ slug: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Merchant Account" value={merchantSelectValue}
              disabled={!merchantsReady}
              onChange={(e) => patch({ merchantId: e.target.value })}
              helperText={!merchantsReady ? 'Loading merchants…' : 'Link merchant for documents & payouts'}>
              <MenuItem value="">— Select merchant —</MenuItem>
              {merchantOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <PhoneNumberField fullWidth label="Mobile" value={form.phone} onChange={(e) => patch({ phone: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="email" label="Email" value={form.email} onChange={(e) => patch({ email: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <PhoneNumberField fullWidth label="Support Number" value={form.supportNumber}
              onChange={(e) => patch({ supportNumber: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Description" value={form.description}
              onChange={(e) => patch({ description: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" value={form.addressLine}
              onChange={(e) => patch({ addressLine: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City" value={form.city} onChange={(e) => patch({ city: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="State" value={form.state} onChange={(e) => patch({ state: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Pincode" value={form.pincode} onChange={(e) => patch({ pincode: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <RestaurantLocationPicker
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={(latitude, longitude) => patch({ latitude, longitude })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ZoneMasterSelect label="Zone" value={form.zoneCode} onChange={(code) => patch({ zoneCode: code })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Warehouse" value={form.warehouseCode}
              onChange={(e) => patch({ warehouseCode: e.target.value })}>
              <MenuItem value="">— None —</MenuItem>
              {(masters?.warehouses || []).map((w) => (
                <MenuItem key={w.code} value={w.code}>{w.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormFileUpload label="Store Logo" value={form.logo} onChange={(url) => patch({ logo: url })}
              folder="grocery/stores/logo"
              imageConstraints={{ minWidth: 200, minHeight: 200, maxWidth: 800, maxHeight: 800, aspectRatio: '1:1' }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormFileUpload label="Cover Image" value={form.coverImage} onChange={(url) => patch({ coverImage: url })}
              folder="grocery/stores/cover"
              imageConstraints={{ minWidth: 600, minHeight: 300, maxWidth: 1600, maxHeight: 900, aspectRatio: '16:9' }} />
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          {form.merchantId && merchantCompliance && hasMerchantCompliance(merchantCompliance) && (
            <Grid item xs={12}>
              <Alert
                severity="success"
                action={(
                  <Stack direction="row" spacing={1}>
                    <Button size="small" color="inherit" onClick={() => applyMerchantCompliance(false)}>Apply compliance</Button>
                    {(merchantCompliance.latitude || merchantCompliance.longitude) && (
                      <Button size="small" color="inherit" onClick={() => applyMerchantCompliance(true)}>+ location</Button>
                    )}
                  </Stack>
                )}
              >
                Approved merchant documents available. GST: {merchantCompliance.gstNumber || '—'} · FSSAI: {merchantCompliance.fssaiNumber || '—'}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth required label="Store Type *"
              value={mastersLoading ? '' : (form.storeTypeCode || '')}
              disabled={mastersLoading || !(masters?.grocery_store_types?.length)}
              onChange={(e) => patch({
                storeTypeCode: e.target.value,
                isDarkStore: e.target.value === 'dark_store' || e.target.value.includes('dark'),
              })}>
              <MenuItem value="">— Select —</MenuItem>
              {(masters?.grocery_store_types || []).map((t) => (
                <MenuItem key={t.code} value={t.code}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {t.metadata?.imageUrl ? (
                      <Avatar src={resolveAssetUrl(String(t.metadata.imageUrl))} sx={{ width: 24, height: 24 }} />
                    ) : null}
                    <span>{t.name}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isDarkStore || isDarkType}
                  disabled={isDarkType}
                  onChange={(e) => patch({ isDarkStore: e.target.checked })}
                />
              )}
              label="Dark Store (quick commerce)"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="GST Number" value={form.gstNumber}
              onChange={(e) => patch({ gstNumber: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="FSSAI Number" value={form.fssaiNumber}
              onChange={(e) => patch({ fssaiNumber: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Trade License" value={form.tradeLicenseNumber}
              onChange={(e) => patch({ tradeLicenseNumber: e.target.value })} />
          </Grid>
        </Grid>
      )}

      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Min Order (₹)" value={form.minOrderAmount}
              onChange={(e) => patch({ minOrderAmount: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Delivery Radius (km)" value={form.deliveryRadius}
              onChange={(e) => patch({ deliveryRadius: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Avg Pick Time (mins)" value={form.avgPickTimeMinutes}
              onChange={(e) => patch({ avgPickTimeMinutes: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={form.deliveryEnabled} onChange={(e) => patch({ deliveryEnabled: e.target.checked })} />}
              label="Home delivery"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={form.pickupEnabled} onChange={(e) => patch({ pickupEnabled: e.target.checked })} />}
              label="Store pickup"
            />
          </Grid>
          <Grid item xs={12} display="flex" gap={2}>
            <FormControlLabel control={<Switch checked={form.isOpen} onChange={(e) => patch({ isOpen: e.target.checked })} />} label="Open Now" />
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => patch({ isActive: e.target.checked })} />} label="Active" />
          </Grid>
        </Grid>
      )}

      {tab === 3 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Opening & Closing Hours</Typography>
              <Button size="small" variant="outlined" onClick={applyMasterHours}>Apply from masters</Button>
            </Stack>
          </Grid>
          {form.openingHours.map((row, i) => {
            const dayLabel = GROCERY_WEEK_DAYS.find((d) => d.value === row.day)?.label || row.day;
            return (
              <Grid container spacing={1} key={row.day} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={2} sm={1}><Typography variant="body2">{dayLabel}</Typography></Grid>
                <Grid item xs={4} sm={3}>
                  <TextField fullWidth size="small" label="Open" value={row.openTime} disabled={row.isClosed}
                    onChange={(e) => updateHour(i, { openTime: e.target.value })} />
                </Grid>
                <Grid item xs={4} sm={3}>
                  <TextField fullWidth size="small" label="Close" value={row.closeTime} disabled={row.isClosed}
                    onChange={(e) => updateHour(i, { closeTime: e.target.value })} />
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel control={<Switch size="small" checked={!!row.isClosed}
                    onChange={(e) => updateHour(i, { isClosed: e.target.checked })} />} label="Closed" />
                </Grid>
              </Grid>
            );
          })}
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Weekly Holiday" value={form.weeklyHoliday}
              onChange={(e) => patch({ weeklyHoliday: e.target.value })}>
              <MenuItem value="">— None —</MenuItem>
              {GROCERY_WEEK_DAYS.map((d) => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      )}

      {tab === 4 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Verification Status" value={form.verificationStatus}
              onChange={(e) => patch({ verificationStatus: e.target.value as GroceryStoreFormState['verificationStatus'] })}>
              {VERIFICATION_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Revenue Model" value={form.revenueModel}
              onChange={(e) => patch({ revenueModel: e.target.value as GroceryStoreFormState['revenueModel'] })}>
              <MenuItem value="commission">Commission</MenuItem>
              <MenuItem value="subscription">Subscription</MenuItem>
            </TextField>
          </Grid>
          {form.revenueModel === 'commission' ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Commission Type" value={form.vendorCommissionType}
                  onChange={(e) => patch({ vendorCommissionType: e.target.value as 'percentage' | 'fixed' })}>
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed (₹)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label={form.vendorCommissionType === 'fixed' ? 'Commission (₹)' : 'Commission (%)'}
                  value={form.vendorCommissionValue}
                  onChange={(e) => patch({ vendorCommissionValue: Number(e.target.value) })} />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <TextField select fullWidth required label="Subscription Plan" value={subscriptionPlanValue}
                disabled={!merchantPlansReady}
                onChange={(e) => patch({ partnerSubscriptionPlanId: e.target.value })}>
                <MenuItem value="">— Select plan —</MenuItem>
                {subscriptionPlanOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      )}

      {tab === 5 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Merchant Documents</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            GST, FSSAI, and trade license documents are managed in Document Management against the linked merchant.
          </Typography>
          <Button
            component={RouterLink}
            to={documentsUrl}
            variant="outlined"
            endIcon={<OpenInNew />}
            disabled={!form.merchantId}
          >
            {form.merchantId ? 'Open merchant documents' : 'Select a merchant first'}
          </Button>
        </Box>
      )}
    </FormDrawer>
  );
}
